import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { useAppSignUpPageResultsUpdateEffect } from "@faire/retailer-visitor-shared/components/AppSignUp/AppSignUpPageResultsProvider";
import { RetailerFooter } from "@faire/retailer-visitor-shared/components/Footer/RetailerFooter";
import { AddedToBagContainer } from "@faire/retailer-visitor-shared/components/Notifications/AddedToBag/AddedToBagStore";
import { useAddedToBag } from "@faire/retailer-visitor-shared/components/Notifications/AddedToBag/useAddedToBag";
import { useCartProductAddOptions } from "@faire/retailer-visitor-shared/features/ProductAdd/useCartProductAddOptions";
import { LazyQuickAddModal } from "@faire/retailer-visitor-shared/features/ProductQuickAddModal/LazyQuickAddModal";
import { canAddToCart } from "@faire/retailer-visitor-shared/lib/canAddToCart";
import { getCartToken } from "@faire/retailer-visitor-shared/serialized-data/getCartToken";
import { trackEditCartItemEvent, trackProductClick } from "@faire/retailer-visitor-shared/services/Events";
import { useTopOfPageContent } from "@faire/retailer-visitor-shared/stores/ui/useTopOfPageContent";
import { useHideUpdateCartModal } from "@faire/retailer-visitor-shared/views/brand/actions/useHideUpdateCartModal";
import { useCartUpdateWithDisplayPromo } from "@faire/retailer-visitor-shared/views/brand/BrandCart/hooks/useCartUpdateWithDisplayPromo";
import { BrandCartEvents } from "@faire/retailer-visitor-shared/views/brand/BrandCart/index";
import { LazyBrandCart } from "@faire/retailer-visitor-shared/views/brand/BrandCart/lazyBrandCart";
import { BrandPageBrandViewClientContextProvider } from "@faire/retailer-visitor-shared/views/brand/contexts/BrandPageBrandViewContext";
import { BrandPageStateContext } from "@faire/retailer-visitor-shared/views/brand/contexts/BrandPageStateContext";
import { useBrandPageStateData } from "@faire/retailer-visitor-shared/views/brand/contexts/useBrandPageStateData";
import { useBrand } from "@faire/retailer-visitor-shared/views/brand/hooks/useBrand";
import { useBrandPageModal } from "@faire/retailer-visitor-shared/views/brand/hooks/useBrandPageModal";
import { useProductTokenForUpdateCartModal } from "@faire/retailer-visitor-shared/views/brand/hooks/useProductTokenForUpdateCartModal";
import { InnerMakerContainer as ProductBasedCollectionInnerContainer } from "@faire/retailer-visitor-shared/views/brand/InnerMakerContainer";
import { MakerContainer as ProductBasedCollectionContainer } from "@faire/retailer-visitor-shared/views/brand/styled";
import { BrandPageModal } from "@faire/retailer-visitor-shared/views/brand/utils/enum";
import { DisplayStickyActionBanner } from "@faire/retailer-visitor-shared/views/product-based-collection/__internal__/DisplayCollectionPromo/DisplayStickyActionBanner";
import { ProductBasedCollectionBreadcrumb } from "@faire/retailer-visitor-shared/views/product-based-collection/__internal__/ProductBasedCollectionBreadcrumb";
import { ProductBasedCollectionErrorContent } from "@faire/retailer-visitor-shared/views/product-based-collection/__internal__/ProductBasedCollectionErrorContent";
import { ProductBasedCollectionPageContent } from "@faire/retailer-visitor-shared/views/product-based-collection/__internal__/ProductBasedCollectionPageContent";
import { useCollectionDisplayPromo } from "@faire/retailer-visitor-shared/views/product-based-collection/useCollectionDisplayPromo";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { trackEvent } from "@faire/web--source/common/trackEvent";
import { Row } from "@faire/web--source/slate/Layout";
import { PageLoader } from "@faire/web--source/ui/CircleSpinner";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useQueryParams } from "@faire/web--source/ui/hooks/useQueryParams";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { useQuery as usePublishedProductCollectionMetadata } from "@faire/web-api--source/api/brand/brandToken/published-product-collection-metadata/get-hooks";
import { QueryParameters as IProductBasedCollectionTokenQueryParams } from "@faire/web-api--source/api/product-based-collections/productBasedCollectionToken/retailer/get";
import { useQuery as useProductBasedCollectionTokenQuery } from "@faire/web-api--source/api/product-based-collections/productBasedCollectionToken/retailer/get-hooks";
import { trackCoreCollectionsQuickAddCollectionPageCheckoutClick } from "@faire/web-api--source/events/coreCollections/click/quickAddCollectionPageCheckout";
import { trackCoreCollectionsStickyCartCollectionPageViewBagClick } from "@faire/web-api--source/events/coreCollections/click/stickyCartCollectionPageViewBag";
import { trackCoreCollectionsCollectionsPageStickyCartView } from "@faire/web-api--source/events/coreCollections/view/collectionsPageStickyCart";
import { trackCoreCollectionsProductBasedCollectionPageView } from "@faire/web-api--source/events/coreCollections/view/productBasedCollectionPage";
import { QueryParameters as ProductBasedCollectionTokenQueryParameters } from "@faire/web-api--source/routes/product-based-collection/product_based_collection_token";
import { observer } from "mobx-react";
import React, { Suspense, useEffect } from "react";
import styled from "styled-components";
var EMPTY_BRAND_TOKEN = "EMPTY";
var EMPTY_PBC_TOKEN = "EMPTY";
var ProductBasedCollectionBase = function(param) {
    var token = param.token;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var highlightedProduct = useQueryParams(ProductBasedCollectionTokenQueryParameters.parse).highlightedProduct;
    var _useProductBasedCollectionTokenQuery = useProductBasedCollectionTokenQuery(token, IProductBasedCollectionTokenQueryParams.build({
        highlighted_product: highlightedProduct
    })), collectionData = _useProductBasedCollectionTokenQuery.data, isCollectionError = _useProductBasedCollectionTokenQuery.isError;
    var collection = collectionData === null || collectionData === void 0 ? void 0 : collectionData.product_collection;
    useAppSignUpPageResultsUpdateEffect({
        productBasedCollection: collection
    });
    var isDesktopAndAbove = useViewport().isDesktopAndAbove;
    var _collection_brand_token;
    var brandPageStateData = useBrandPageStateData((_collection_brand_token = collection === null || collection === void 0 ? void 0 : collection.brand_token) !== null && _collection_brand_token !== void 0 ? _collection_brand_token : "");
    useEffect(function() {
        if (collection === null || collection === void 0 ? void 0 : collection.brand_token) {
            var _collectionData_collection_promo;
            if (canAddToCart() && isDesktopAndAbove) {
                trackCoreCollectionsCollectionsPageStickyCartView();
            }
            var _collectionData_collection_promo_brand_promo_collection_token;
            trackCoreCollectionsProductBasedCollectionPageView(collection.brand_token, token, (_collectionData_collection_promo_brand_promo_collection_token = collectionData === null || collectionData === void 0 ? void 0 : (_collectionData_collection_promo = collectionData.collection_promo) === null || _collectionData_collection_promo === void 0 ? void 0 : _collectionData_collection_promo.brand_promo_collection_token) !== null && _collectionData_collection_promo_brand_promo_collection_token !== void 0 ? _collectionData_collection_promo_brand_promo_collection_token : "");
        }
    // UpdateCartItem function is causing flaky sticky cart on collection page as its references changes every time the cart being modified. Will ignore this dependency before we get rid of brand cart view state.
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        collection === null || collection === void 0 ? void 0 : collection.brand_token,
        isDesktopAndAbove,
        token
    ]);
    if (isCollectionError) {
        var errorMessage = strictLocalize({
            id: "N2SuwA",
            defaultMessage: [
                {
                    type: 0,
                    value: "Failed to fetch collection "
                },
                {
                    type: 1,
                    value: "apparelCollectionToken"
                }
            ]
        }, {
            apparelCollectionToken: token
        });
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(ProductBasedCollectionErrorContent, {
                    errorMessage: errorMessage
                }),
                /*#__PURE__*/ _jsx(RetailerFooter, {})
            ]
        });
    }
    if (collection === undefined) {
        return /*#__PURE__*/ _jsx(PageLoader, {});
    }
    var _collection_brand_token1;
    return /*#__PURE__*/ _jsx(BrandPageStateContext.Provider, {
        value: brandPageStateData,
        children: /*#__PURE__*/ _jsx(BrandPageBrandViewClientContextProvider, {
            brandToken: (_collection_brand_token1 = collection === null || collection === void 0 ? void 0 : collection.brand_token) !== null && _collection_brand_token1 !== void 0 ? _collection_brand_token1 : "",
            children: /*#__PURE__*/ _jsx(ProductBaseCollectionContent, {
                token: token
            })
        })
    });
};
var ProductBaseCollectionContent = function(param) {
    var token = param.token;
    var _collectionData_product_collection, _collectionData_product_collection1, _collectionMetaData_product_based_collections;
    var highlightedProduct = useQueryParams(ProductBasedCollectionTokenQueryParameters.parse).highlightedProduct;
    var headerPlusPromoBannerHeight = useTopOfPageContent().headerPlusPromoBannerHeight;
    var _useProductBasedCollectionTokenQuery = useProductBasedCollectionTokenQuery(token, IProductBasedCollectionTokenQueryParams.build({
        highlighted_product: highlightedProduct
    })), collectionData = _useProductBasedCollectionTokenQuery.data;
    var collection = collectionData === null || collectionData === void 0 ? void 0 : collectionData.product_collection;
    var _collectionData_product_tiles;
    var productTiles = (_collectionData_product_tiles = collectionData === null || collectionData === void 0 ? void 0 : collectionData.product_tiles) !== null && _collectionData_product_tiles !== void 0 ? _collectionData_product_tiles : [];
    var _collectionData_show_vertical_imagery;
    var showVerticalImagery = (_collectionData_show_vertical_imagery = collectionData === null || collectionData === void 0 ? void 0 : collectionData.show_vertical_imagery) !== null && _collectionData_show_vertical_imagery !== void 0 ? _collectionData_show_vertical_imagery : true;
    var _collectionData_product_collection_brand_token;
    var _usePublishedProductCollectionMetadata = usePublishedProductCollectionMetadata((_collectionData_product_collection_brand_token = collectionData === null || collectionData === void 0 ? void 0 : (_collectionData_product_collection = collectionData.product_collection) === null || _collectionData_product_collection === void 0 ? void 0 : _collectionData_product_collection.brand_token) !== null && _collectionData_product_collection_brand_token !== void 0 ? _collectionData_product_collection_brand_token : "", undefined, {
        enabled: !!(collectionData === null || collectionData === void 0 ? void 0 : (_collectionData_product_collection1 = collectionData.product_collection) === null || _collectionData_product_collection1 === void 0 ? void 0 : _collectionData_product_collection1.brand_token)
    }), collectionMetaData = _usePublishedProductCollectionMetadata.data;
    var _collectionMetaData_product_based_collections_filter;
    var otherCollections = (_collectionMetaData_product_based_collections_filter = collectionMetaData === null || collectionMetaData === void 0 ? void 0 : (_collectionMetaData_product_based_collections = collectionMetaData.product_based_collections) === null || _collectionMetaData_product_based_collections === void 0 ? void 0 : _collectionMetaData_product_based_collections.filter(function(thisCollection) {
        return thisCollection.product_based_collection_token !== (collection === null || collection === void 0 ? void 0 : collection.token);
    })) !== null && _collectionMetaData_product_based_collections_filter !== void 0 ? _collectionMetaData_product_based_collections_filter : [];
    var updateItems = useCartUpdateWithDisplayPromo(collection).updateItems;
    var _useCollectionDisplayPromo = useCollectionDisplayPromo(collection), isCollectionDisplayPromoActive = _useCollectionDisplayPromo.isCollectionDisplayPromoActive, isQualifiedForCollectionDisplayPromo = _useCollectionDisplayPromo.isQualifiedForCollectionDisplayPromo, fetchDisplayPromoProgress = _useCollectionDisplayPromo.fetchDisplayPromoProgress;
    // The query will automatically run when enabled
    // You can still manually refetch if needed:
    useEffect(function() {
        fetchDisplayPromoProgress();
    }, [
        fetchDisplayPromoProgress
    ]);
    var _useViewport = useViewport(), isDesktopAndAbove = _useViewport.isDesktopAndAbove, isMobile = _useViewport.isMobile, isTabletAndBelow = _useViewport.isTabletAndBelow;
    var ignoreContainer = useAddedToBag().ignoreContainer;
    var productAddOptions = useCartProductAddOptions(updateItems);
    var brand = useBrand();
    var brandPageModal = useBrandPageModal();
    var productTokenForUpdateCartModal = useProductTokenForUpdateCartModal();
    var onHideUpdateCartModal = useHideUpdateCartModal();
    var viewRef = useDoActionWhenVisible(function() {
        ignoreContainer(AddedToBagContainer.BRAND_CART, !isMobile);
    });
    var handleBrandCartEvents = function(param) {
        var event = param.event, token = param.token, quantities = param.quantities, message = param.message;
        switch(event){
            case BrandCartEvents.OPEN_PRODUCT_MODAL:
                if (token) {
                    trackProductClick(token, "collection_page_sidebar_cart_cart_item");
                }
                break;
            case BrandCartEvents.CHANGE_CART_QUANTITY:
                if (quantities && token) {
                    var containerName = quantities.newValue === 0 ? "collection_page_sidebar_cart" : "collection_page_sidebar_cart_dropdown";
                    trackEditCartItemEvent(quantities.oldValue, quantities.newValue, containerName, token);
                }
                break;
            case BrandCartEvents.GO_TO_BAG:
                var _collection_token, _collection_brand_token;
                trackCoreCollectionsStickyCartCollectionPageViewBagClick((_collection_token = collection === null || collection === void 0 ? void 0 : collection.token) !== null && _collection_token !== void 0 ? _collection_token : EMPTY_PBC_TOKEN, (_collection_brand_token = collection === null || collection === void 0 ? void 0 : collection.brand_token) !== null && _collection_brand_token !== void 0 ? _collection_brand_token : EMPTY_BRAND_TOKEN);
                break;
            case BrandCartEvents.CHECKOUT:
                var _collection_token1, _collection_brand_token1;
                trackCoreCollectionsQuickAddCollectionPageCheckoutClick((_collection_token1 = collection === null || collection === void 0 ? void 0 : collection.token) !== null && _collection_token1 !== void 0 ? _collection_token1 : EMPTY_PBC_TOKEN, (_collection_brand_token1 = collection === null || collection === void 0 ? void 0 : collection.brand_token) !== null && _collection_brand_token1 !== void 0 ? _collection_brand_token1 : EMPTY_BRAND_TOKEN, getCartToken());
                break;
            case BrandCartEvents.CHECKOUT_ERROR:
                trackEvent("/collection/cart/checkout-button/shake/".concat(message));
                break;
        }
    };
    if (collection === undefined) {
        return /*#__PURE__*/ _jsx(PageLoader, {});
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(ProductBasedCollectionUpperPart, {
                ref: viewRef,
                "data-test-id": "collections-page-sticky-cart-wrapper",
                children: [
                    brandPageModal === BrandPageModal.UPDATE_CART && productTokenForUpdateCartModal ? /*#__PURE__*/ _jsx(Suspense, {
                        fallback: null,
                        children: /*#__PURE__*/ _jsx(LazyQuickAddModal, {
                            onClose: onHideUpdateCartModal,
                            productToken: productTokenForUpdateCartModal,
                            productAddOptions: productAddOptions,
                            updateCartFn: updateItems
                        })
                    }) : null,
                    /*#__PURE__*/ _jsx(ProductBasedCollectionContainer, {
                        children: /*#__PURE__*/ _jsxs(ProductBasedCollectionInnerContainer, {
                            children: [
                                /*#__PURE__*/ _jsx(ProductBasedCollectionBreadcrumb, {
                                    collection: collection
                                }),
                                /*#__PURE__*/ _jsx(ProductBasedCollectionPageContent, {
                                    collection: collection,
                                    otherCollections: otherCollections,
                                    productTiles: productTiles,
                                    showVerticalImagery: showVerticalImagery,
                                    collectionPromo: collectionData === null || collectionData === void 0 ? void 0 : collectionData.collection_promo
                                })
                            ]
                        })
                    }),
                    canAddToCart() && isDesktopAndAbove && brand ? /*#__PURE__*/ _jsx(Suspense, {
                        fallback: null,
                        children: /*#__PURE__*/ _jsx(LazyBrandCart, {
                            top: headerPlusPromoBannerHeight,
                            brandToken: brand.token,
                            onEvent: handleBrandCartEvents,
                            collection: collection
                        })
                    }) : null
                ]
            }),
            /*#__PURE__*/ _jsx(RetailerFooter, {}),
            isTabletAndBelow && isCollectionDisplayPromoActive && isQualifiedForCollectionDisplayPromo ? /*#__PURE__*/ _jsx(DisplayStickyActionBanner, {
                collection: collection,
                updateItems: updateItems
            }) : null
        ]
    });
};
export var ProductBasedCollection = observer(ProductBasedCollectionBase);
var ProductBasedCollectionUpperPart = styled(Row).attrs({
    align: "flex-start"
}).withConfig({
    componentId: "sc-35dd252a-0"
})([
    "width:100%;margin:0 auto;padding-bottom:0;border-bottom:1px solid ",
    ";"
], Core.surface.subdued);
